import { useCurrentStateAndParams } from "@uirouter/react";

import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import type { IAutoCompleteSiteItem } from "app/@types/autocomplete";
import { translate } from "app/filters/i18nFilter";
import GradientButton from "app/pages/sales-intelligence/modules/ai-agent/components/GradientButton/GradientButton";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import NotEnoughQuotaTooltipContainer from "app/pages/sales-intelligence/modules/quota/containers/NotEnoughQuotaTooltipContainer/NotEnoughQuotaTooltipContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import EmailCompanyAutocomplete from "app/pages/sales-intelligence/pages/ai-agent/containers/CompanyAutocomplete/AICompanyAutocomplete";
import { Recipients } from "app/pages/sales-intelligence/pages/ai-agent/containers/Recipients/Recipients";
import { useIsContactsAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-tab/hooks/useIsContactsAvailable";

import {
    RECIPIENT_CONTACTS_DROPDOWN_WIDTH,
    RECIPIENT_CONTACTS_INPUT_FIELD_WIDTH,
    StyledRecipientTextField,
} from "./styles";

type AIAgentEmailToolbarProps = {
    recents: IAutoCompleteSiteItem[];
    selected: AiAgentEmailAutocompleteCompany | null;
    onSelect: (c: AiAgentEmailAutocompleteCompany | null) => void;
    recipient: Contact | null;
    onRecipientChange: (v: Contact | null) => void;
    onGenerate: () => void;
    isSubmitButtonDisabled?: boolean;
};

const AIAgentEmailToolbar = ({
    recents,
    selected,
    onSelect,
    recipient,
    onRecipientChange,
    onGenerate,
    isSubmitButtonDisabled,
}: AIAgentEmailToolbarProps) => {
    const { quota, isLoading: isLoadingDataCredits, isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);
    const isDisabled = isSubmitButtonDisabled || isBlocked || isLoadingDataCredits;

    const handleGenerate = () => {
        if (isDisabled) {
            return;
        }
        onGenerate();
        AIAgentTrackingService.email.trackGenerateEmail();
    };

    const {
        params: { domain },
    } = useCurrentStateAndParams<{ domain: string }>();

    const { isContactsVisible, isContactsStatusFetched } = useIsContactsAvailable();
    const shouldUseContactsDropdown = isContactsStatusFetched && isContactsVisible;

    const handleRecipientTextChange = (value: string) => {
        onRecipientChange(value ? { name: value } : null);
    };

    const renderGenerateButton = () =>
        isBlocked ? (
            <NotEnoughQuotaTooltipContainer quota={quota}>
                <div>
                    <GradientButton
                        variant="generate"
                        onClick={handleGenerate}
                        disabled
                        dataAutomation="generate-button"
                    >
                        {translate("si.pages.ai_agent.form.button.generate")}
                    </GradientButton>
                </div>
            </NotEnoughQuotaTooltipContainer>
        ) : (
            <GradientButton
                variant="generate"
                onClick={handleGenerate}
                disabled={isDisabled}
                dataAutomation="generate-button"
            >
                {translate("si.pages.ai_agent.form.button.generate")}
            </GradientButton>
        );

    return (
        <Flex
            width="100%"
            gap="24px"
            alignItems="flex-end"
            flexWrap="nowrap"
        >
            <Box
                width="220px"
                flexShrink={0}
                data-automation="company-autocomplete"
            >
                <Text
                    fontVariant="caption"
                    color={colorsPalettes.carbon[300]}
                    margin="0 0 4px"
                    display="block"
                >
                    {translate("si.pages.ai_agent_email.company_autocomplete.label")}
                </Text>
                <EmailCompanyAutocomplete
                    recents={recents}
                    onSelect={onSelect}
                    selectedItem={selected}
                    isOpenByDefault={!domain}
                    isDisabled={isBlocked || isLoadingDataCredits}
                />
            </Box>

            {shouldUseContactsDropdown ? (
                <Box
                    width={RECIPIENT_CONTACTS_DROPDOWN_WIDTH}
                    flexShrink={0}
                    data-automation="recipient-autocomplete"
                >
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[300]}
                        margin="0 0 4px"
                        display="block"
                    >
                        {translate("si.pages.ai_agent_email.recipient.label")}
                    </Text>
                    <Recipients
                        value={recipient}
                        onChange={onRecipientChange}
                        domain={selected?.domain || ""}
                        isDisabled={isBlocked || isLoadingDataCredits}
                    />
                </Box>
            ) : (
                <Box
                    width={RECIPIENT_CONTACTS_INPUT_FIELD_WIDTH}
                    flexShrink={0}
                    data-automation="recipient-input-field"
                >
                    <Text
                        fontVariant="caption"
                        color={colorsPalettes.carbon[300]}
                        margin="0 0 4px"
                        display="block"
                    >
                        {translate("si.pages.ai_agent_email.recipient.label")}
                    </Text>
                    <StyledRecipientTextField
                        value={recipient?.name || ""}
                        placeholder={translate("si.pages.ai_agent_email.recipient.placeholder")}
                        type="text"
                        onChange={handleRecipientTextChange}
                        data-automation="recipient-text-input"
                    />
                </Box>
            )}
            <Box>{renderGenerateButton()}</Box>
        </Flex>
    );
};

export default AIAgentEmailToolbar;
